import { Box, SxProps } from '@mui/system';
import React from 'react';

import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';

const BORDER_RADIUS = '2px';
const CIRCLE_DIAMETER = 448;
const CIRCLE_RADIUS = CIRCLE_DIAMETER / 2; // Half of circle diameter
const LABEL_DISTANCE = 90; // Distance of labels from circle edge

const POINT_ADJUSTMENTS = {
  5: {
    '0': {
      radiusOffset: -38,
      angleOffset: 0,
      sx: {
        alignItems: 'center'
      }
    },
    '2': {
      sx: {
        alignItems: 'center'
      }
    },
    '3': {
      sx: {
        alignItems: 'center'
      }
    }
  },

  6: {
    '0': {
      radiusOffset: -38,
      angleOffset: 0,
      sx: {
        alignItems: 'center'
      }
    },
    '3': {
      radiusOffset: -33,
      angleOffset: 0
    }
  },

  7: {
    // by index
    '0': {
      radiusOffset: -38,
      angleOffset: 0,
      sx: {
        alignItems: 'center'
      }
    },
    '1': {
      radiusOffset: 38,
      angleOffset: 0.24
    },
    '2': {
      radiusOffset: 18,
      angleOffset: 0
    },
    '3': {
      radiusOffset: 13,
      angleOffset: -0.11
    },
    '4': {
      radiusOffset: 4,
      angleOffset: 0.07
    },
    '5': {
      radiusOffset: 19,
      angleOffset: 0
    },
    '6': {
      radiusOffset: 48,
      angleOffset: -0.24
    }
  }
};

interface PositionAdjustment {
  radiusOffset?: number;
  angleOffset?: number;
  sx?: SxProps;
}

interface SpiderChartLoadingSkeletonProps {
  numPoints?: number;
  pointAdjustments?: Record<number, PositionAdjustment>;
}

const calculatePosition = (
  angle: number,
  radius: number,
  index: number,
  numPoints: number,
  customAdjustments?: Record<number, PositionAdjustment>
) => {
  // First check predefined adjustments, then fall back to custom adjustments
  const predefinedAdjustments = POINT_ADJUSTMENTS[numPoints as keyof typeof POINT_ADJUSTMENTS];
  const adjustment = (predefinedAdjustments?.[index] || customAdjustments?.[index]) ?? {};

  const adjustedRadius = radius + (adjustment.radiusOffset || 0);
  const adjustedAngle = angle + (adjustment.angleOffset || 0);

  return {
    left: `calc(50% + ${Math.cos(adjustedAngle) * adjustedRadius}px)`,
    top: `calc(50% + ${Math.sin(adjustedAngle) * adjustedRadius}px)`,
    transform: 'translate(-50%, -50%)'
  };
};

export const SpiderChartLoadingSkeleton = ({ numPoints = 0, pointAdjustments }: SpiderChartLoadingSkeletonProps) => {
  const skeletonPoints = React.useMemo(() => {
    return Array.from({ length: numPoints }, () => ({
      labelWidth: 152,
      valueWidth: 120
    }));
  }, [numPoints]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '448px',
        height: '448px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Center circular skeleton */}
      <SlSkeleton variant="circular" width={448} height={448} />

      {/* Surrounding skeletons */}
      {skeletonPoints.map((point, index) => {
        const angle = (index * 2 * Math.PI) / skeletonPoints.length - Math.PI / 2;
        const position = calculatePosition(angle, CIRCLE_RADIUS + LABEL_DISTANCE, index, numPoints, pointAdjustments);

        // Get the predefined adjustments for current number of points
        const predefinedAdjustments = POINT_ADJUSTMENTS[numPoints as keyof typeof POINT_ADJUSTMENTS];
        const adjustment = predefinedAdjustments?.[index];

        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              ...position,
              ...(adjustment?.sx ?? {})
            }}
          >
            <SlSkeleton
              sx={{ borderRadius: BORDER_RADIUS }}
              variant="rectangular"
              width={point.labelWidth}
              height={16}
            />
            <SlSkeleton
              sx={{ borderRadius: BORDER_RADIUS }}
              variant="rectangular"
              width={point.valueWidth}
              height={32}
            />
          </Box>
        );
      })}
    </Box>
  );
};
